import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Activity, Search, Filter, RefreshCw, Clock, User, FileText,
  Shield, GitMerge, DollarSign, Settings, Eye, ChevronDown,
  ChevronUp
} from 'lucide-react';

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [expandedLogs, setExpandedLogs] = useState({});

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, entityFilter]);

  const fetchLogs = async () => {
    try {
      const response = await adminAPI.getAuditLogs();
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];
    
    if (searchTerm) {
      filtered = filtered.filter(l => 
        l.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.entity_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.actor_user_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (entityFilter !== 'all') {
      filtered = filtered.filter(l => l.entity_type === entityFilter);
    }
    
    setFilteredLogs(filtered);
  };

  const toggleExpand = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      deal: FileText,
      lsp_verification: Shield,
      commitment: GitMerge,
      payment: DollarSign,
      user: User,
    };
    return icons[entityType] || Settings;
  };

  const getEntityColor = (entityType) => {
    const colors = {
      deal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      lsp_verification: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      commitment: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      payment: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      user: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
    };
    return colors[entityType] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
  };

  const formatAction = (action) => {
    return action
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (loading) {
    return (
      <Layout title="Audit Logs">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Audit Logs">
      <div className="space-y-6 animate-slide-in-up" data-testid="admin-audit-logs">
        {/* Filters */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by action, entity ID, or user ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="audit-search"
                />
              </div>
              <div className="w-48">
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                  <SelectTrigger data-testid="entity-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by entity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entities</SelectItem>
                    <SelectItem value="deal">Deals</SelectItem>
                    <SelectItem value="lsp_verification">LSP Verifications</SelectItem>
                    <SelectItem value="commitment">Commitments</SelectItem>
                    <SelectItem value="payment">Payments</SelectItem>
                    <SelectItem value="user">Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchLogs} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Logs</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{logs.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Deal Actions</p>
              <p className="text-2xl font-bold text-blue-600">{logs.filter(l => l.entity_type === 'deal').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">LSP Actions</p>
              <p className="text-2xl font-bold text-purple-600">{logs.filter(l => l.entity_type === 'lsp_verification').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Commitments</p>
              <p className="text-2xl font-bold text-green-600">{logs.filter(l => l.entity_type === 'commitment').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Payments</p>
              <p className="text-2xl font-bold text-amber-600">{logs.filter(l => l.entity_type === 'payment').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Logs List */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <Activity className="w-5 h-5 text-orange-500" />
              Activity Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="py-12 text-center">
                <Activity className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-400">No audit logs found</p>
                <p className="text-sm text-slate-500 mt-1">Activity will appear here as actions are performed</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLogs.map((log, index) => {
                  const Icon = getEntityIcon(log.entity_type);
                  const isExpanded = expandedLogs[log.id];
                  
                  return (
                    <div 
                      key={log.id || index} 
                      className="relative pl-8 pb-3 border-l-2 border-slate-200 dark:border-slate-700 last:border-l-0"
                      data-testid={`audit-log-${log.id || index}`}
                    >
                      {/* Timeline dot */}
                      <div className={`absolute left-[-9px] w-4 h-4 rounded-full ${getEntityColor(log.entity_type)} flex items-center justify-center`}>
                        <div className="w-2 h-2 bg-current rounded-full" />
                      </div>
                      
                      <div 
                        className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        onClick={() => toggleExpand(log.id || index)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getEntityColor(log.entity_type)}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-slate-100">
                                {formatAction(log.action)}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {log.entity_type.replace('_', ' ')} • {log.entity_id.slice(0, 12)}...
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <Clock className="w-4 h-4" />
                            {formatTimestamp(log.timestamp)}
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 animate-slide-in-up">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Actor ID</p>
                                <p className="font-mono text-slate-900 dark:text-slate-100 text-xs">{log.actor_user_id}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Entity ID</p>
                                <p className="font-mono text-slate-900 dark:text-slate-100 text-xs">{log.entity_id}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Timestamp</p>
                                <p className="text-slate-900 dark:text-slate-100">{new Date(log.timestamp).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Entity Type</p>
                                <p className="text-slate-900 dark:text-slate-100">{log.entity_type}</p>
                              </div>
                            </div>
                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                              <div className="mt-3">
                                <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">Metadata</p>
                                <pre className="bg-slate-100 dark:bg-slate-900 p-2 rounded text-xs overflow-x-auto text-slate-900 dark:text-slate-100">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminAuditLogs;
